import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const ColorLabel = ({ panColor, panNumber, caseId, onUpdate }) => {
  const [selectedColor, setSelectedColor] = useState(panColor || '#800080');
  const [currentPenNumber, setCurrentPenNumber] = useState(panNumber || '');
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const predefinedColors = [
    { name: 'Purple', value: '#800080' },
    { name: 'Magenta', value: '#FF00FF' },
    { name: 'Red', value: '#FF0000' },
    { name: 'Maroon', value: '#800000' },
    { name: 'Brown', value: '#A52A2A' },
    { name: 'Orange', value: '#FFA500' },
    { name: 'Yellow', value: '#FFFF00' },
    { name: 'Lime', value: '#00FF00' },
    { name: 'Green', value: '#008000' },
    { name: 'Olive', value: '#808000' },
    { name: 'Teal', value: '#008080' },
    { name: 'Cyan', value: '#00FFFF' },
    { name: 'Blue', value: '#0000FF' },
    { name: 'Navy', value: '#000080' },
    { name: 'Silver', value: '#C0C0C0' },
    { name: 'Gray', value: '#808080' },
    { name: 'Black', value: '#000000' }
  ];

  useEffect(() => {
    setSelectedColor(panColor || '#800080');
    setCurrentPenNumber(panNumber || '');
  }, [panColor, panNumber]);

  const handlePenNumberChange = (e) => {
    setCurrentPenNumber(e.target.value);
  };

  const handlePenColorChange = (newColor) => {
    setSelectedColor(newColor);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!caseId) {
      setError('Case ID is missing');
      return;
    }

    setIsLoading(true);
    setError(null);

    const colorName = predefinedColors.find(
      color => color.value === selectedColor
    )?.name;

    if (!colorName) {
      setError('Color not found');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}/color-pan`,
        {
          color: colorName,
          panNumber: currentPenNumber
        }
      );

      if (response.data && response.data.case) {
        onUpdate(response.data.case);
        closeModal();
      }
    } catch (err) {
      console.error('Error updating color/pan:', err);
      setError(
        err.response?.data?.message ||
        'Failed to update color and pan number'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const colorName =
    predefinedColors.find(
      color => color.value === selectedColor
    )?.name || '';

  return (
    <>
      <style>
        {`
          /* ================================
             COLOR PEN BUTTON - DESKTOP & MOBILE
             ================================ */
          .color-pen-button {
            cursor: pointer;
            background-color: transparent;   /* background removed */
            color: #004aad;                  /* icon color */
            padding: 4px 10px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            font-size: 13px;
            line-height: 1.4;
          }

          .color-pen-button svg {
            font-size: 18px;
            color: #004aad;                  /* icon color */
          }

          @media (max-width: 600px) {
            .color-pen-button {
              padding: 1px 5px;
              font-size: 10px;
              line-height: 1;
            }

            .color-pen-button svg {
              font-size: 10px;
              color: #004aad;
            }
          }

          /* ================================
             RESPONSIVE MODAL STYLING
             ================================ */
          .custom-react-modal {
            width: 320px;
            max-width: 90%;
            margin: auto;
            border-radius: 6px;
            padding: 15px;
            height: auto;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2);
            background: white;
            position: relative;
            outline: none;
          }
        `}
      </style>

      {/* =================================
          COLOR SECTION
          (Font/Weight/Size SAME, sirf spacing aligned)
          - Removed top: -30px
          - Removed extra padding
          ================================= */}
      <div
        style={{
          position: "relative",
          top: "0",
          margin: "0",
          padding: "0",
          textAlign: "left",
        }}
      >
        <p
          style={{
            fontWeight: 'bold',
            fontSize: "18px",
            margin: "0 0 2px 0",
            padding: "0",
            textAlign: "left",
          }}
        >
          Color:
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            margin: "0",
            padding: "0",
          }}
        >
          <div
            style={{
              backgroundColor: selectedColor,
              width: '28px',
              height: '16px',
              border: '1px solid #ddd',
              borderRadius: '3px',
            }}
          ></div>

          <span
            style={{
              fontWeight: 'bold',
              fontSize: "14px",
              lineHeight: "1.2",
              margin: "0",
              padding: "0",
            }}
          >
            {colorName}
          </span>

          <button
            onClick={openModal}
            className="color-pen-button"
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
        </div>

        <Modal
          isOpen={showModal}
          onRequestClose={closeModal}
          contentLabel="Edit Pen Details"
          className="custom-react-modal"
          style={{
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }
          }}
        >
          <button
            className="close-btn mb-4"
            onClick={closeModal}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#aaa',
              fontSize: '2em',
              float: "right",
              lineHeight: "1"
            }}
          >
            &times;
          </button>

          <h5 className='mt-2' style={{ fontWeight: 'bold' }}>
            Edit Pen Details
          </h5>

          {error && (
            <div
              className="alert alert-danger"
              role="alert"
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              {error}
            </div>
          )}

          <div style={{ marginBottom: '15px', marginTop: '15px' }}>
            <label style={{ fontWeight: '500', marginBottom: '5px', display: 'block' }}>Pen Number:</label>
            <input
              type="text"
              className='form-control'
              value={currentPenNumber}
              onChange={handlePenNumberChange}
              style={{ marginLeft: '0' }}
            />
          </div>

          <div>
            <label style={{ fontWeight: '500' }}>Pen Color:</label>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                marginTop: '10px'
              }}
            >
              {predefinedColors.map((color) => (
                <div
                  key={color.name}
                  style={{
                    backgroundColor: color.value,
                    width: '22px',
                    height: '22px',
                    margin: '4px',
                    cursor: 'pointer',
                    borderRadius: '3px',
                    display: 'inline-block',
                    border:
                      selectedColor === color.value
                        ? '2px solid #004aad'
                        : '1px solid #ddd'
                  }}
                  onClick={() =>
                    handlePenColorChange(color.value)
                  }
                  title={color.name}
                />
              ))}
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              marginTop: '20px',
              backgroundColor: '#004aad',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              width: '100%',
              fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </Modal>
      </div>
    </>
  );
};

export default ColorLabel;